"use client";

import React from 'react';
import { CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const EDWExplainedSection = () => {
  const benefits = [
    "Removes conflicting numbers between teams",
    "Enables faster reporting and real-time insights",
    "Provides role-based dashboards for every department",
    "Improves governance and compliance readiness",
    "Prepares organizations for predictive analytics and AI initiatives"
  ];

  return (
    <section className="bg-cream py-12 sm:py-16 md:py-24 min-h-0 flex items-center pt-24 sm:pt-28 md:pt-32">
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        {/* Eyebrow */}
        <p className="text-gold text-xs sm:text-sm font-semibold uppercase tracking-[1.5px] text-left md:text-center mb-3 sm:mb-4">
          ENTERPRISE DATA WAREHOUSING EXPLAINED
        </p>

        {/* Headline */}
        <h1 className="font-serif text-navy text-[26px] sm:text-[34px] md:text-[44px] lg:text-[48px] leading-[1.2] font-bold text-left md:text-center mb-4 sm:mb-6">
          What is Enterprise Data Warehousing?
        </h1>

        {/* Subheadline */}
        <p className="text-charcoal text-[15px] sm:text-base md:text-xl leading-[1.7] text-left md:text-center max-w-4xl md:mx-auto mb-8 sm:mb-12">
          One system that collects, cleans, and stores all company data–a single source of truth for reporting, analytics, and AI.
        </p>

        {/* Body Content */}
        <div className="max-w-5xl mx-auto mb-10 sm:mb-16">
          {/* Benefits List */}
          <div className="bg-white rounded-xl p-5 sm:p-6 md:p-8 shadow-sm border border-gray-200">
            <h3 className="font-serif text-navy text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Key Benefits of EDW Implementation:</h3>
            <div className="space-y-3 sm:space-y-4">
              {benefits?.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gold flex-shrink-0 mt-0.5" />
                  <p className="text-charcoal text-[14px] sm:text-base leading-[1.6]">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Visual Transformation Diagram */}
        <div className="max-w-6xl mx-auto">
          <h3 className="font-serif text-navy text-xl sm:text-2xl font-bold text-left md:text-center mb-6 sm:mb-10">
            The Transformation Journey
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 items-center">
            {/* Before State */}
            <div className="bg-white rounded-xl p-4 sm:p-6 border-2 border-red-200 shadow-sm">
              <div className="text-center mb-4">
                <span className="inline-block bg-red-100 text-red-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
                  Before
                </span>
              </div>
              <h4 className="font-serif text-navy text-base sm:text-lg font-bold mb-3 sm:mb-4 text-center">Disconnected Systems</h4>
              <div className="space-y-2 sm:space-y-3">
                <div className="bg-gray-100 rounded-lg p-2.5 sm:p-3 text-center">
                  <p className="text-charcoal text-xs sm:text-sm font-medium">Excel Spreadsheets</p>
                </div>
                <div className="bg-gray-100 rounded-lg p-2.5 sm:p-3 text-center">
                  <p className="text-charcoal text-xs sm:text-sm font-medium">CRM System</p>
                </div>
                <div className="bg-gray-100 rounded-lg p-2.5 sm:p-3 text-center">
                  <p className="text-charcoal text-xs sm:text-sm font-medium">ERP Database</p>
                </div>
                <div className="bg-gray-100 rounded-lg p-2.5 sm:p-3 text-center">
                  <p className="text-charcoal text-xs sm:text-sm font-medium">Marketing Tools</p>
                </div>
                <div className="bg-gray-100 rounded-lg p-2.5 sm:p-3 text-center">
                  <p className="text-charcoal text-xs sm:text-sm font-medium">Finance Systems</p>
                </div>
              </div>
              <p className="text-red-600 text-[11px] sm:text-xs text-center mt-3 sm:mt-4 font-medium">Conflicting data, slow reporting, manual work</p>
            </div>

            {/* Arrow */}
            <div className="flex justify-center">
              <ArrowRightIcon className="w-12 h-12 text-gold hidden md:block" />
              <div className="md:hidden flex flex-col items-center">
                <div className="w-0.5 h-12 bg-gold"></div>
                <div className="w-3 h-3 border-t-2 border-r-2 border-gold transform rotate-135 -mt-1"></div>
              </div>
            </div>

            {/* After State */}
            <div className="bg-white rounded-xl p-4 sm:p-6 border-2 border-gold shadow-lg">
              <div className="text-center mb-4">
                <span className="inline-block bg-gold/10 text-gold text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
                  After
                </span>
              </div>
              <h4 className="font-serif text-navy text-base sm:text-lg font-bold mb-3 sm:mb-4 text-center">Unified EDW Layer</h4>
              
              {/* Central EDW Box */}
              <div className="bg-gradient-to-br from-navy to-navy/90 rounded-lg p-4 sm:p-6 mb-3 sm:mb-4 shadow-md">
                <p className="text-white text-center font-bold text-sm sm:text-base mb-1 sm:mb-2">Enterprise Data Warehouse</p>
                <p className="text-white/80 text-[11px] sm:text-xs text-center">Single Source of Truth</p>
              </div>

              {/* Centralized Dashboards */}
              <div className="space-y-2">
                <div className="bg-gold/10 rounded-lg p-2.5 sm:p-3 border border-gold/30">
                  <p className="text-navy text-xs sm:text-sm font-medium text-center">Executive Dashboards</p>
                </div>
                <div className="bg-gold/10 rounded-lg p-2.5 sm:p-3 border border-gold/30">
                  <p className="text-navy text-xs sm:text-sm font-medium text-center">Real-Time Analytics</p>
                </div>
                <div className="bg-gold/10 rounded-lg p-2.5 sm:p-3 border border-gold/30">
                  <p className="text-navy text-xs sm:text-sm font-medium text-center">Role-Based Reports</p>
                </div>
              </div>
              
              <p className="text-gold text-[11px] sm:text-xs text-center mt-3 sm:mt-4 font-medium">Trusted data, instant insights, confident decisions</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EDWExplainedSection;